import { useState } from 'react';
import type { WaitlistEntry } from '@salonomy/types';
import { Button, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { useConvertWaitlistEntry } from '../../data/waitlist.js';
import { clockLabel, longDate, wallMinutesToUtcIso } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS10(a) — "Book it": turn an OFFERED waitlist entry into a real appointment. The entry already
 * carries the wanted DATE, service and (optional) stylist; the only thing missing is the concrete
 * START TIME, so this dialog is deliberately small — a single time picker — mirroring JoinWaitlist's
 * modal shape. We build the UTC `startAt` from the wanted date + chosen wall time exactly like
 * NewAppointmentModal (wallMinutesToUtcIso). `endAt` is left to the server to derive from the service
 * duration, so we never do client-side span math here.
 */

/** HH:MM → minutes-from-midnight (mirror of NewAppointmentModal's parser). */
function parseTime(value: string): number | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!m) return null;
  return +m[1]! * 60 + +m[2]!;
}

export function ConvertWaitlistModal({
  entry,
  serviceName,
  staffName,
  tz,
  onClose,
}: {
  entry: WaitlistEntry;
  /** Resolver from the panel (id → display name) so we don't re-fetch. */
  serviceName: (id: string) => string;
  staffName: (id: string | null) => string;
  tz: string;
  onClose: () => void;
}) {
  const convert = useConvertWaitlistEntry();
  const toast = useToast();
  const [time, setTime] = useState('');

  const startMin = parseTime(time);
  const valid = startMin != null;

  async function submit() {
    if (startMin == null) {
      toast.error('Pick a valid start time.');
      return;
    }
    try {
      const appt = await convert.mutateAsync({
        id: entry.id,
        // The wanted date + chosen wall time → the concrete UTC instant. endAt omitted: the server
        // derives it from the service duration (the convert handler reads service/staff off the entry).
        startAt: wallMinutesToUtcIso(entry.wantedDate, startMin, tz),
      });
      void appt;
      toast.success(`Booked ${entry.clientName} for ${longDate(entry.wantedDate, tz)}`);
      onClose();
    } catch {
      // The server rejects an unavailable slot (409 clash) — surface it honestly rather than silently.
      toast.error('Could not book that time — it may already be taken. Try another.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Book the offered slot"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid || convert.isPending}>
            {convert.isPending ? 'Booking…' : 'Book it'}
          </Button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        Turn <strong>{entry.clientName}</strong>’s waitlist offer into a real appointment on{' '}
        {longDate(entry.wantedDate, tz)}. Pick the start time — we’ll work out the end from the
        service.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        <Pill tone="soft" className="mono">
          {serviceName(entry.serviceId)}
        </Pill>
        <Pill tone="soft" className="mono">
          {staffName(entry.staffId)}
        </Pill>
        {entry.renterId ? (
          <Pill tone="soft" className="mono">
            sealed
          </Pill>
        ) : null}
      </div>

      <Field
        label="Start time"
        hint={
          startMin != null
            ? `Booking from ${clockLabel(startMin)} on ${longDate(entry.wantedDate, tz)}.`
            : 'When should the appointment start?'
        }
      >
        <TextInput autoFocus type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </Field>
    </Modal>
  );
}
